import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Genera un PDF de recibo de comprobante de pago.
 * Retorna { pdfUrl, pdfFileName, pdfDoc } para previsualización y descarga.
 */
const generarReciboPdf = async (comprobante, headers) => {
  // 1. Obtener configuración del sistema
  let config = {};
  try {
    const configResponse = await axios.get(`${API}/api/definiciones/configuracion/actual`, { headers });
    config = configResponse.data || {};
  } catch {
    try {
      const configResponseArray = await axios.get(`${API}/api/definiciones/configuracion`, { headers });
      const configArray = configResponseArray.data || [];
      config = Array.isArray(configArray) && configArray.length > 0 ? configArray[0] : {};
    } catch {}
  }

  // 2. Obtener datos del contribuyente
  let laeNumero = "No Aplica";
  let direccionFiscal = "No especificada";

  if (comprobante.contribuyente_id) {
    try {
      const conLaeResponse = await axios.get(`${API}/api/contribuyentes/con-lae`, { headers });
      const contribuyenteConLae = conLaeResponse.data.find(c => c.id === comprobante.contribuyente_id);
      if (contribuyenteConLae) {
        laeNumero = contribuyenteConLae.nro_lae || contribuyenteConLae.numero_lae || "No Aplica";
        direccionFiscal = contribuyenteConLae.domicilio_fiscal || contribuyenteConLae.direccion || "No especificada";
      } else {
        const sinLaeResponse = await axios.get(`${API}/api/contribuyentes/sin-lae`, { headers });
        const contribuyenteSinLae = sinLaeResponse.data.find(c => c.id === comprobante.contribuyente_id);
        if (contribuyenteSinLae) {
          direccionFiscal = contribuyenteSinLae.domicilio_fiscal || contribuyenteSinLae.direccion || "No especificada";
        }
      }
    } catch {}
  }

  // 3. Obtener código catastral y dirección catastral
  let codigoCatastral = "No Aplica";
  let direccionCatastral = "No Aplica";

  if (comprobante.rif) {
    try {
      const fichasResponse = await axios.get(`${API}/api/catastro/ficha-catastral`, { headers });
      const fichaCatastral = fichasResponse.data.find(f => f.rif === comprobante.rif);
      if (fichaCatastral) {
        codigoCatastral = fichaCatastral.codigo_catastral || "No Aplica";
        direccionCatastral = fichaCatastral.direccion_ubicacion || fichaCatastral.direccion || fichaCatastral.ubicacion || "No Aplica";
      }
    } catch {}
  }

  // 4. Obtener liquidaciones asociadas
  let liquidaciones = [];
  let descuento = 0;
  let multa = 0;

  let tiposImpuestoData = [];
  try {
    const tiposResponse = await axios.get(`${API}/api/definiciones/tipos-impuesto`, { headers });
    tiposImpuestoData = tiposResponse.data || [];
  } catch {}

  let codigosPresupuestariosData = [];
  try {
    const codigosResponse = await axios.get(`${API}/api/definiciones/codigos-presupuestarios`, { headers });
    codigosPresupuestariosData = codigosResponse.data || [];
  } catch {}

  let clasificadorInmueblesData = [];
  try {
    const clasifResponse = await axios.get(`${API}/api/catastro/clasificador-inmuebles`, { headers });
    clasificadorInmueblesData = clasifResponse.data || [];
  } catch {}

  const obtenerCodigoPresupuestario = (tipoImpuesto) => {
    if (!tipoImpuesto) return "-";
    if (tipoImpuesto.codigo_presupuestario_id) {
      const codigoObj = codigosPresupuestariosData.find(c => c.id === tipoImpuesto.codigo_presupuestario_id);
      if (codigoObj && codigoObj.codigo_presupuestario) return codigoObj.codigo_presupuestario;
    }
    return tipoImpuesto.codigo_presupuestario || tipoImpuesto.cod_presupuestario || "-";
  };

  if (comprobante.tramite_por_pagar_id) {
    try {
      const liquidacionesResponse = await axios.get(`${API}/api/liquidaciones/datos-declaracion`, { headers });
      const liquidacion = liquidacionesResponse.data.find(l => l.id === comprobante.tramite_por_pagar_id);
      if (liquidacion && liquidacion.lineas_impuestos) {
        const mesAnioDeclaracion = liquidacion.mes_anio_declaracion || "-";
        const nroPlanilla = liquidacion.nro_planilla || "-";

        liquidaciones = liquidacion.lineas_impuestos.map(linea => {
          const concepto = linea.impuesto_tasa || linea.concepto || "-";
          let codPresupuestario = "-";

          if (concepto.toUpperCase().includes("IMPUESTO INMOBILIARIO")) {
            const partes = concepto.split(" - ");
            if (partes.length > 1) {
              const nombreClasificador = partes[partes.length - 1].trim();
              const clasificador = clasificadorInmueblesData.find(c =>
                c.uso_inmueble && c.uso_inmueble.toUpperCase() === nombreClasificador.toUpperCase()
              );
              if (clasificador && clasificador.codigo_presupuestario) codPresupuestario = clasificador.codigo_presupuestario;
            }
          } else {
            let tipoImpuesto = tiposImpuestoData.find(t => t.id === linea.tipo_impuesto_id);
            if (!tipoImpuesto && concepto) {
              tipoImpuesto = tiposImpuestoData.find(t =>
                t.nombre === concepto || t.nombre?.toLowerCase() === concepto?.toLowerCase()
              );
            }
            codPresupuestario = obtenerCodigoPresupuestario(tipoImpuesto);
          }

          return {
            mes_anio_declaracion: mesAnioDeclaracion,
            nro_planilla: nroPlanilla,
            cod_presupuestario: codPresupuestario,
            concepto: linea.impuesto_tasa || linea.concepto || "-",
            base_imponible: linea.ingreso_bruto || 0,
            porcentaje: linea.importe || 0,
            monto: linea.sub_total || 0
          };
        });
        descuento = liquidacion.descuento || 0;
        multa = liquidacion.multa || 0;
      }
    } catch {}
  }

  if (liquidaciones.length === 0) {
    liquidaciones = [{
      mes_anio_declaracion: "-", nro_planilla: "-", cod_presupuestario: "-",
      concepto: "Pago de tributo", base_imponible: comprobante.monto || 0, porcentaje: "-", monto: comprobante.monto || 0
    }];
  }

  // 5. Obtener cuenta recaudadora
  let cuentaRecaudadora = "-";
  if (comprobante.cuenta_recaudadora_descripcion) {
    cuentaRecaudadora = comprobante.cuenta_recaudadora_descripcion;
  } else if (comprobante.cuenta_recaudadora_id) {
    try {
      const cuentasResponse = await axios.get(`${API}/api/definiciones/cuentas-bancarias`, { headers });
      const cuenta = cuentasResponse.data.find(c => c.id === comprobante.cuenta_recaudadora_id);
      if (cuenta) cuentaRecaudadora = cuenta.descripcion || "-";
    } catch {}
  }

  // 6. Generar QR Code
  let qrCodeDataUrl = null;
  try {
    const qrData = [
      config.nombre_superintendencia || "DIRECCIÓN DE TRIBUTOS",
      `RECIBO NRO.: ${comprobante.nro_recibo || "-"}`,
      `FECHA: ${comprobante.fecha_recaudacion || "-"}`,
      `TOTAL: ${(comprobante.monto || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs.`
    ].join('\n');
    qrCodeDataUrl = await QRCode.toDataURL(qrData, { width: 100, margin: 1 });
  } catch {}

  // 7. Crear PDF (media carta = 215.9mm x 139.7mm)
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [139.7, 215.9] });

  const pageWidth = 215.9;
  const pageHeight = 139.7;
  const margin = 6;
  const primaryColor = [25, 55, 109];

  // Banda superior decorativa
  doc.setFillColor(25, 55, 109);
  doc.rect(0, 0, pageWidth, 2.5, 'F');
  doc.setFillColor(180, 150, 60);
  doc.rect(0, 2.5, pageWidth, 0.8, 'F');

  // Logo institucional
  const logoUrl = config.logo_encabezado_derecho || config.logo_encabezado_izquierdo;
  if (logoUrl) {
    try {
      let imageFormat = 'PNG';
      if (logoUrl.includes('data:image/jpeg') || logoUrl.includes('.jpg') || logoUrl.includes('.jpeg')) imageFormat = 'JPEG';
      if (logoUrl.startsWith('data:image/')) {
        doc.addImage(logoUrl, imageFormat, margin, margin + 4, 22, 22);
      } else {
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = logoUrl; });
        doc.addImage(img, imageFormat, margin, margin + 4, 22, 22);
      }
    } catch {}
  }

  // Encabezado institucional centrado
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(25, 55, 109);
  doc.text("REPÚBLICA BOLIVARIANA DE VENEZUELA", pageWidth / 2, margin + 6, { align: "center" });

  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  doc.text(config.nombre_alcaldia || "ALCALDÍA BOLIVARIANA", pageWidth / 2, margin + 10, { align: "center" });

  doc.setFontSize(6);
  doc.setFont("helvetica", "normal");
  doc.text(config.nombre_superintendencia || "DIRECCIÓN DE ADMINISTRACIÓN TRIBUTARIA", pageWidth / 2, margin + 14, { align: "center" });

  // Título del documento
  doc.setFillColor(25, 55, 109);
  doc.roundedRect(pageWidth / 2 - 25, margin + 16, 50, 6, 1, 1, 'F');
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("COMPROBANTE DE PAGO", pageWidth / 2, margin + 20.5, { align: "center" });
  doc.setTextColor(0, 0, 0);

  // QR Code (derecha)
  if (qrCodeDataUrl) {
    try {
      doc.setDrawColor(25, 55, 109);
      doc.setLineWidth(0.4);
      doc.roundedRect(pageWidth - margin - 22, margin + 4, 22, 22, 1.5, 1.5, 'S');
      doc.addImage(qrCodeDataUrl, 'PNG', pageWidth - margin - 21, margin + 5, 20, 20);
    } catch {}
  }

  // Número de recibo y fecha
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(25, 55, 109);
  doc.text(`N° ${comprobante.nro_recibo || "-"}`, pageWidth - margin - 11, margin + 29, { align: "center" });
  doc.setFontSize(5.5);
  doc.setTextColor(80, 80, 80);
  doc.setFont("helvetica", "normal");
  doc.text(`${comprobante.fecha_recaudacion || "-"}`, pageWidth - margin - 11, margin + 32, { align: "center" });

  // Sección de datos del contribuyente
  let yPos = margin + 28;
  doc.setFillColor(248, 249, 252);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2 - 30, 17, 1.5, 1.5, 'F');

  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(25, 55, 109);
  doc.text("DATOS DEL CONTRIBUYENTE", margin + 2, yPos + 3);

  yPos += 6;
  doc.setFontSize(5.5);
  doc.setTextColor(50, 50, 50);

  doc.setFont("helvetica", "bold");
  doc.text("Contribuyente:", margin + 2, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(String(comprobante.nombre_razon_social || "-").substring(0, 40), margin + 20, yPos);

  doc.setFont("helvetica", "bold");
  doc.text("RIF:", margin + 80, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(String(comprobante.rif || "-"), margin + 87, yPos);

  doc.setFont("helvetica", "bold");
  doc.text("L.A.E:", margin + 110, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(String(laeNumero), margin + 120, yPos);

  doc.setFont("helvetica", "bold");
  doc.text("Cód. Catastral:", margin + 140, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(String(codigoCatastral), margin + 160, yPos);

  yPos += 4;
  doc.setFont("helvetica", "bold");
  doc.text("Dir. Fiscal:", margin + 2, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(String(direccionFiscal).toUpperCase().substring(0, 95), margin + 18, yPos);

  yPos += 4;
  doc.setFont("helvetica", "bold");
  doc.text("Dir. Catastral:", margin + 2, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(String(direccionCatastral).toUpperCase().substring(0, 90), margin + 21, yPos);

  // Tabla de conceptos
  yPos += 4;
  autoTable(doc, {
    startY: yPos,
    head: [['MES/AÑO DECL.', 'NRO PLANILLA', 'CÓD. PRESUP.', 'CONCEPTO', 'BASE IMPON.', '%', 'MONTO (Bs.)']],
    body: liquidaciones.map(l => [
      String(l.mes_anio_declaracion), String(l.nro_planilla), String(l.cod_presupuestario),
      String(l.concepto).substring(0, 28),
      typeof l.base_imponible === 'number' ? l.base_imponible.toLocaleString('es-VE', { minimumFractionDigits: 2 }) : String(l.base_imponible),
      typeof l.porcentaje === 'number' ? l.porcentaje.toString() : String(l.porcentaje),
      typeof l.monto === 'number' ? l.monto.toLocaleString('es-VE', { minimumFractionDigits: 2 }) : String(l.monto)
    ]),
    margin: { left: margin, right: margin },
    styles: { fontSize: 5, cellPadding: 1, lineColor: [200, 200, 200], lineWidth: 0.1 },
    headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 5, halign: 'center' },
    alternateRowStyles: { fillColor: [252, 252, 254] },
    columnStyles: {
      0: { cellWidth: 22, halign: 'center' }, 1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 22, halign: 'center' }, 3: { cellWidth: 50 },
      4: { cellWidth: 22, halign: 'right' }, 5: { cellWidth: 10, halign: 'center' },
      6: { cellWidth: 22, halign: 'right' }
    }
  });

  // Resumen financiero
  let finalY = (doc.lastAutoTable?.finalY || yPos + 15) + 2;
  const resumenWidth = 48;
  const resumenX = pageWidth - margin - resumenWidth;
  doc.setFillColor(248, 249, 252);
  doc.roundedRect(resumenX, finalY - 1, resumenWidth, 14, 1.5, 1.5, 'F');

  doc.setFontSize(5.5);
  doc.setTextColor(50, 50, 50);

  const totalMonto = liquidaciones.reduce((sum, l) => sum + (typeof l.monto === 'number' ? l.monto : 0), 0);
  const totalACancelar = comprobante.monto || (totalMonto - descuento + multa);

  doc.setFont("helvetica", "normal");
  doc.text("Descuento:", resumenX + 2, finalY + 2);
  doc.text(`${descuento.toLocaleString('es-VE', { minimumFractionDigits: 2 })}`, resumenX + resumenWidth - 2, finalY + 2, { align: "right" });

  doc.text("Multa:", resumenX + 2, finalY + 5.5);
  doc.text(`${multa.toLocaleString('es-VE', { minimumFractionDigits: 2 })}`, resumenX + resumenWidth - 2, finalY + 5.5, { align: "right" });

  doc.setDrawColor(180, 150, 60);
  doc.setLineWidth(0.3);
  doc.line(resumenX + 2, finalY + 7, resumenX + resumenWidth - 2, finalY + 7);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(6);
  doc.setTextColor(25, 55, 109);
  doc.text("TOTAL:", resumenX + 2, finalY + 11);
  doc.text(`${totalACancelar.toLocaleString('es-VE', { minimumFractionDigits: 2 })} Bs.`, resumenX + resumenWidth - 2, finalY + 11, { align: "right" });

  // Sección de datos del pago
  const pagoTitleY = finalY + 16;
  doc.setFillColor(25, 55, 109);
  doc.roundedRect(margin, pagoTitleY - 1, 30, 4, 0.8, 0.8, 'F');
  doc.setFontSize(5.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("DATOS DEL PAGO", margin + 1.5, pagoTitleY + 1.8);
  doc.setTextColor(0, 0, 0);

  const pagoY = pagoTitleY + 4;
  autoTable(doc, {
    startY: pagoY,
    head: [['FECHA', 'BANCO EMISOR', 'CUENTA RECAUDADORA', 'REFERENCIA', 'MONTO (Bs.)']],
    body: [[
      String(comprobante.fecha_recaudacion || "-"),
      String(comprobante.banco_emisor_nombre || "-").substring(0, 22),
      String(cuentaRecaudadora).substring(0, 22),
      String(comprobante.nro_referencia || "-"),
      (comprobante.monto || 0).toLocaleString('es-VE', { minimumFractionDigits: 2 })
    ]],
    margin: { left: margin, right: margin },
    styles: { fontSize: 5.5, cellPadding: 1, lineColor: [200, 200, 200], lineWidth: 0.1 },
    headStyles: { fillColor: [70, 85, 110], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 5.5, halign: 'center' },
    columnStyles: {
      0: { cellWidth: 24, halign: 'center' }, 1: { cellWidth: 42 },
      2: { cellWidth: 45 }, 3: { cellWidth: 28, halign: 'center' },
      4: { cellWidth: 26, halign: 'right' }
    }
  });

  // Pie de página
  const footerY = pageHeight - 6;
  doc.setDrawColor(180, 150, 60);
  doc.setLineWidth(0.4);
  doc.line(margin, footerY - 2, pageWidth - margin, footerY - 2);
  doc.setDrawColor(25, 55, 109);
  doc.setLineWidth(0.2);
  doc.line(margin, footerY - 1.4, pageWidth - margin, footerY - 1.4);

  doc.setFontSize(4.5);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(100, 100, 100);
  let usuarioNombre = "Usuario";
  try {
    const usuario = JSON.parse(localStorage.getItem("user") || "{}");
    usuarioNombre = usuario.nombre || usuario.email || "Usuario";
  } catch {}
  doc.text(`Impreso: ${usuarioNombre} | ${new Date().toLocaleString('es-VE')}`, margin, footerY);

  doc.setFont("helvetica", "normal");
  const rif_alcaldia = config.rif_alcaldia || config.rif_institucion || "G-20000000-0";
  const nombre_alcaldia = config.nombre_alcaldia || "ALCALDÍA BOLIVARIANA";
  doc.text(`${rif_alcaldia} - ${nombre_alcaldia}`, pageWidth / 2, footerY, { align: "center" });
  doc.text("Pág. 1/1", pageWidth - margin, footerY, { align: "right" });

  // Bandas inferiores
  doc.setFillColor(180, 150, 60);
  doc.rect(0, pageHeight - 0.8, pageWidth, 0.8, 'F');
  doc.setFillColor(25, 55, 109);
  doc.rect(0, pageHeight - 2.5, pageWidth, 1.7, 'F');

  // Marca de agua
  doc.setTextColor(235, 235, 235);
  doc.setFontSize(50);
  doc.setFont("helvetica", "bold");
  doc.text((comprobante.estatus || "PENDIENTE").toUpperCase(), pageWidth / 2, pageHeight / 2, { align: "center", angle: 30 });
  doc.setTextColor(0, 0, 0);

  // Generar blob
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const pdfFileName = `Recibo_${comprobante.nro_recibo || "sin_numero"}.pdf`;

  return { pdfUrl, pdfFileName, pdfDoc: doc };
};

export default generarReciboPdf;
